/**
 * HR Request Types — stałe statusów i typów pól.
 *
 * Definicje kategorii, typów wniosków i pul saldowych zostały przeniesione
 * do hrConfigService (localStorage) i są zarządzane dynamicznie.
 */

// ─── Status wniosku (State Machine) ─────────────────────────────
export const HR_REQUEST_STATUS = {
    DRAFT: "Draft",
    PENDING_MANAGER: "PendingManager",
    PENDING_DIRECTOR: "PendingDirector",
    APPROVED_BY_MANAGER: "ApprovedByManager",
    APPROVED_BY_DIRECTOR: "ApprovedByDirector",
    REJECTED_BY_MANAGER: "RejectedByManager",
    REJECTED_BY_DIRECTOR: "RejectedByDirector",
    PENDING_HR: "PendingHr",
    COMPLETED: "Completed",
    REJECTED: "Rejected",
    CANCELLED: "Cancelled",
    CORRECTION_REQUESTED: "CorrectionRequested",
    // Nowe statusy dla elastycznego systemu kroków
    PENDING_CFO: "PendingCfo",
    PENDING_PRESIDENT: "PendingPresident",
    PENDING_EMPLOYEE_ACK: "PendingEmployeeAck",
    APPROVED_BY_CFO: "ApprovedByCfo",
    APPROVED_BY_PRESIDENT: "ApprovedByPresident",
    REJECTED_BY_CFO: "RejectedByCfo",
    REJECTED_BY_PRESIDENT: "RejectedByPresident",
    EMPLOYEE_ACKNOWLEDGED: "EmployeeAcknowledged",
    RESUBMITTED: "Resubmitted",
};

export const HR_STATUS_MAP = {
    [HR_REQUEST_STATUS.DRAFT]: {
        label: "Szkic",
        color: "#94A3B8",
        bg: "rgba(148, 163, 184, 0.08)",
    },
    [HR_REQUEST_STATUS.PENDING_MANAGER]: {
        label: "Oczekuje na przełożonego",
        color: "#F59E0B",
        bg: "rgba(245, 158, 11, 0.08)",
    },
    [HR_REQUEST_STATUS.PENDING_DIRECTOR]: {
        label: "Oczekuje na dyrektora",
        color: "#F59E0B",
        bg: "rgba(245, 158, 11, 0.08)",
    },
    [HR_REQUEST_STATUS.APPROVED_BY_MANAGER]: {
        label: "Zaakceptowany przez przełożonego",
        color: "#3B82F6",
        bg: "rgba(59, 130, 246, 0.08)",
    },
    [HR_REQUEST_STATUS.APPROVED_BY_DIRECTOR]: {
        label: "Zaakceptowany przez dyrektora",
        color: "#3B82F6",
        bg: "rgba(59, 130, 246, 0.08)",
    },
    [HR_REQUEST_STATUS.REJECTED_BY_MANAGER]: {
        label: "Odrzucony przez przełożonego",
        color: "#EF4444",
        bg: "rgba(239, 68, 68, 0.08)",
    },
    [HR_REQUEST_STATUS.REJECTED_BY_DIRECTOR]: {
        label: "Odrzucony przez dyrektora",
        color: "#EF4444",
        bg: "rgba(239, 68, 68, 0.08)",
    },
    [HR_REQUEST_STATUS.PENDING_HR]: {
        label: "Oczekuje na HR",
        color: "#8B5CF6",
        bg: "rgba(139, 92, 246, 0.08)",
    },
    [HR_REQUEST_STATUS.COMPLETED]: {
        label: "Zrealizowany",
        color: "#10B981",
        bg: "rgba(16, 185, 129, 0.08)",
    },
    [HR_REQUEST_STATUS.REJECTED]: {
        label: "Odrzucony",
        color: "#EF4444",
        bg: "rgba(239, 68, 68, 0.08)",
    },
    [HR_REQUEST_STATUS.CANCELLED]: {
        label: "Anulowany",
        color: "#94A3B8",
        bg: "rgba(148, 163, 184, 0.08)",
    },
    [HR_REQUEST_STATUS.CORRECTION_REQUESTED]: {
        label: "Do korekty",
        color: "#D97706",
        bg: "rgba(217, 119, 6, 0.08)",
    },
    [HR_REQUEST_STATUS.PENDING_CFO]: {
        label: "Oczekuje na Dyr. Finansowego",
        color: "#F59E0B",
        bg: "rgba(245, 158, 11, 0.08)",
    },
    [HR_REQUEST_STATUS.PENDING_PRESIDENT]: {
        label: "Oczekuje na Prezesa",
        color: "#F59E0B",
        bg: "rgba(245, 158, 11, 0.08)",
    },
    [HR_REQUEST_STATUS.PENDING_EMPLOYEE_ACK]: {
        label: "Oczekuje na pracownika",
        color: "#F59E0B",
        bg: "rgba(245, 158, 11, 0.08)",
    },
    [HR_REQUEST_STATUS.APPROVED_BY_CFO]: {
        label: "Zaakceptowany przez Dyr. Fin.",
        color: "#3B82F6",
        bg: "rgba(59, 130, 246, 0.08)",
    },
    [HR_REQUEST_STATUS.APPROVED_BY_PRESIDENT]: {
        label: "Zaakceptowany przez Prezesa",
        color: "#3B82F6",
        bg: "rgba(59, 130, 246, 0.08)",
    },
    [HR_REQUEST_STATUS.REJECTED_BY_CFO]: {
        label: "Odrzucony przez Dyr. Fin.",
        color: "#EF4444",
        bg: "rgba(239, 68, 68, 0.08)",
    },
    [HR_REQUEST_STATUS.REJECTED_BY_PRESIDENT]: {
        label: "Odrzucony przez Prezesa",
        color: "#EF4444",
        bg: "rgba(239, 68, 68, 0.08)",
    },
    [HR_REQUEST_STATUS.EMPLOYEE_ACKNOWLEDGED]: {
        label: "Pracownik potwierdził",
        color: "#10B981",
        bg: "rgba(16, 185, 129, 0.08)",
    },
    [HR_REQUEST_STATUS.RESUBMITTED]: {
        label: "Ponownie złożony",
        color: "#3B82F6",
        bg: "rgba(59, 130, 246, 0.08)",
    },
};

export const getStatusInfo = (status) =>
    HR_STATUS_MAP[status] || { label: "Nieznany", color: "#94A3B8", bg: "rgba(148, 163, 184, 0.08)" };

// ─── Kod typu "Wycofanie wniosku" ───────────────────────────────
export const WITHDRAWAL_TYPE_CODE = "WITHDRAWAL";

// ─── Typy pól formularza ─────────────────────────────────────────
export const FIELD_TYPE = {
    DATE: "date",
    DATE_RANGE: "dateRange",
    TIME: "time",
    TEXT: "text",
    TEXTAREA: "textarea",
    SELECT: "select",
    USER_PICKER: "userPicker",
    NUMBER: "number",
    CHECKBOX: "checkbox",
    FILE: "file",
    IBAN: "iban",
    INFO: "info",
    HIDDEN: "hidden",
    // [PAKIET 2026-09-02, FUNDAMENT §4] Trzy typy dołożone dla „Karty zatrudnienia" (HIRE_ONBOARDING).
    // Lustro serwera: Infrastructure/Helpers/HrFieldSchema.AcceptedFieldTypes — typ, którego nie ma po
    // TAMTEJ stronie, normalizacja zapisu po cichu zamienia na „text", więc obie listy muszą iść razem.
    // COMPANY_PICKER / DEPARTMENT_PICKER zapisują wartość ZŁOŻONĄ { id, name } (jak USER_PICKER):
    // dokument drukuje nazwę, a sprawa onboardingowa czyta id. MULTISELECT zapisuje TABLICĘ wartości opcji.
    COMPANY_PICKER: "companyPicker",
    DEPARTMENT_PICKER: "departmentPicker",
    MULTISELECT: "multiselect",
    // [DELEGACJE 2026-09-12, doc 37 §4.5] Trzy typy, bez których rozliczenia nie da się wpisać:
    // REPEATER — tabela pozycji (N wierszy o kolumnach z definicji pola, wartość = tablica obiektów),
    // MONEY — kwota z walutą (wartość = liczba; waluta w osobnym kluczu `${name}Waluta`, gdy pole ma listę walut),
    // DATETIME — data z godziną razem (wartość = "YYYY-MM-DDTHH:mm"; silnik diet liczy różnicę dwóch MOMENTÓW).
    // Lustro serwera: HrFieldSchema.AcceptedFieldTypes — typ nieznany po tamtej stronie spada po cichu na „text".
    REPEATER: "repeater",
    MONEY: "money",
    DATETIME: "datetime",
};

/** Typy komórek dopuszczalne w kolumnie pola tabelarycznego (lustro HrFieldSchema.TypyKolumnRepeatera). */
export const REPEATER_COLUMN_TYPES = ["text", "number", "money", "date", "datetime", "select", "checkbox"];

// ─── KATALOG PREDEFINIOWANYCH PÓL ────────────────────────────────
// Pola definiowane w systemie (nie free-text) — admin wybiera je z katalogu, dziedzicząc kanoniczne `name`
// oczekiwane przez backend (dateFrom/dateTo/date/workDays sterują liczeniem dni urlopu, rokiem salda i eksportem
// do kalendarza). Dzięki temu ten sam koncept ma to samo `name` we wszystkich wnioskach — koniec kolizji.
// `computed` = pole wyliczane automatycznie (nieedytowalne przez pracownika). `group` = sekcja w pickerze.
export const HR_FIELD_CATALOG = [
    // Terminy (kanoniczne — używane przez logikę HR)
    { key: "date", label: "Data", type: "date", group: "Terminy", hint: "Pojedyncza data (np. dzień wolny na żądanie)." },
    { key: "dateFrom", label: "Data od", type: "date", group: "Terminy", hint: "Początek zakresu (urlop/nieobecność)." },
    { key: "dateTo", label: "Data do", type: "date", group: "Terminy", hint: "Koniec zakresu." },
    { key: "workDays", label: "Liczba dni roboczych", type: "number", computed: true, group: "Terminy", hint: "Wyliczane automatycznie z zakresu dat (bez weekendów i świąt)." },
    { key: "timeFrom", label: "Godzina od", type: "time", group: "Terminy" },
    { key: "timeTo", label: "Godzina do", type: "time", group: "Terminy" },
    // Uzasadnienie / opis
    { key: "reason", label: "Uzasadnienie / powód", type: "textarea", group: "Opis" },
    { key: "notes", label: "Uwagi", type: "textarea", group: "Opis" },
    { key: "substitute", label: "Zastępstwo (osoba)", type: "text", group: "Opis" },
    // Kadry / finanse
    { key: "bankAccount", label: "Numer konta bankowego", type: "iban", group: "Kadry / finanse" },
    { key: "pesel", label: "PESEL", type: "text", group: "Kadry / finanse" },
    { key: "amount", label: "Kwota", type: "number", group: "Kadry / finanse" },
    { key: "targetEmployee", label: "Pracownik (którego dotyczy)", type: "userPicker", group: "Kadry / finanse" },
    // [PAKIET 2026-09-02] Pola kanoniczne „Karty zatrudnienia" — te same `name`, których oczekuje sprawa
    // onboardingowa przy przepisywaniu wniosku na teczkę wstępną. Wpisane z palca „dzial" zamiast
    // „department" oznaczałoby wniosek, który się złoży i sprawy nie założy.
    { key: "hiringCompany", label: "Podmiot zatrudniający", type: "companyPicker", group: "Kadry / finanse" },
    { key: "department", label: "Dział (docelowy)", type: "departmentPicker", group: "Kadry / finanse", hint: "Dział, do którego trafia nowa osoba — zakres zawęża serwer do działów w Twoim zasięgu." },
    // Potwierdzenia / załączniki
    { key: "confirmed", label: "Potwierdzam / oświadczam", type: "checkbox", group: "Potwierdzenia" },
    { key: "attachment", label: "Załącznik", type: "file", group: "Potwierdzenia" },
    // Praca zdalna
    { key: "remoteLocation", label: "Miejsce pracy zdalnej", type: "text", group: "Praca zdalna" },
    { key: "remoteType", label: "Rodzaj pracy zdalnej", type: "select", group: "Praca zdalna",
        options: [{ value: "occasional", label: "Okazjonalna" }, { value: "scheduled", label: "Planowa" }] },
];
